import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { skipToken } from '@reduxjs/toolkit/query'

import { Button, Stack, Typography } from '@mui/material'

import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'
import PageContainer from '@/components/ui/containers/PageContainer'

import { useGetAdminEstablishmentsQuery } from '@/core/services/admin'
import { useDeleteContractMutation, useGetContractQuery, useUpdateContractMutation } from '@/core/services/contract'
import { useGetLicensesQuery } from '@/core/services/license'

import { getApiErrorMessage } from '@/utils/errors'

import ContractForm from './components/ContractForm'
import { useGetStaffsByEstablishmentQuery } from '@/core/services/staff'

export default function ContractPage() {
  const navigate = useNavigate()
  const { contractId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const { data: contract, isLoading: isFetchingContract } = useGetContractQuery(contractId ?? skipToken)
  const { data: establishments } = useGetAdminEstablishmentsQuery()
  const { data: licenses } = useGetLicensesQuery({})
  const { data: staffs } = useGetStaffsByEstablishmentQuery(contract?.establishment?._id ?? skipToken)
  const [updateContract, { isLoading: isUpdatingContract }] = useUpdateContractMutation()
  const [deleteContract, { isLoading: isDeletingContract }] = useDeleteContractMutation()

  const [editable, setEditable] = useState(false)
  const [openDeleteConfirmationDialog, setOpenDeleteConfirmationDialog] = useState(false)

  const establishmentOptions = useMemo(
    () =>
      establishments
        ?.filter((establishment) => !establishment.contract || establishment._id === contract?.establishment._id)
        .map((establishment) => ({
          label: establishment.name,
          value: establishment._id,
        })) ?? [],
    [establishments]
  )

  const initialValues = useMemo(
    () =>
      contract
        ? {
            establishment: contract.establishment._id,
            licenseUsages: contract.licenseUsages.map(({ license: licenseId, quantity, licensees }) => ({
              license: { id: licenseId, name: licenses?.find((license) => license._id === licenseId)?.name ?? '' },
              quantity,
              licensees,
            })),
            name: contract.name,
          }
        : undefined,
    [contract, licenses]
  )

  const licenseOptions = useMemo(
    () =>
      licenses
        ?.filter((license) => !license.isDefault)
        .map((license) => ({
          label: license.name,
          value: license._id,
        })) ?? [],
    [licenses]
  )

  const staffOptions = useMemo(
    () =>
      staffs?.map((staff) => ({
        label: `${staff.userInfo.name} ${staff.userInfo.surname}`,
        value: staff._id,
      })) ?? [],
    [staffs]
  )

  if (isFetchingContract) {
    return null
  }

  if (!contract) return null

  return (
    <PageContainer>
      <Stack spacing={4}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h2">{contract.name}</Typography>
          <Stack direction="row" spacing={2}>
            <Button color="error" onClick={() => setOpenDeleteConfirmationDialog(true)} variant="contained">
              {t('common:button.delete.label')}
            </Button>
            <Button onClick={() => setEditable(!editable)} variant="contained">
              {!editable ? t('common:button.modify.label') : t('common:button.cancel.label')}
            </Button>
          </Stack>
        </Stack>
        <Stack spacing={3}>
          <ContractForm
            key={editable ? 'editable' : 'readOnly'}
            establishmentOptions={establishmentOptions}
            initialValues={initialValues}
            isSubmitting={isUpdatingContract}
            licenseOptions={licenseOptions}
            onSubmit={async (values) => {
              try {
                await updateContract({
                  id: contract._id,
                  name: values.name,
                  establishment: values.establishment,
                  licenseUsages: values.licenseUsages.map(({ license, quantity, licensees }) => ({
                    license: license.id,
                    quantity,
                    licensees,
                  })),
                }).unwrap()
                setEditable(false)
              } catch (error) {
                enqueueSnackbar(getApiErrorMessage(error, t('notification.contract.update.error')), { variant: 'error' })
              }
            }}
            readOnly={!editable}
            staffOptions={staffOptions}
          />
        </Stack>
      </Stack>
      <DeleteConfirmationDialog
        isDeleting={isDeletingContract}
        maxWidth={800}
        onClose={() => {
          setOpenDeleteConfirmationDialog(false)
        }}
        onDelete={async () => {
          try {
            await deleteContract(contract._id).unwrap()
            setOpenDeleteConfirmationDialog(false)
            navigate('/super-admin/contracts')
          } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, t('notification.contract.delete.error')), { variant: 'error' })
          }
        }}
        open={openDeleteConfirmationDialog}
        title={t('dialog.deleteConfirmation.contract.title')}
        message={t('dialog.deleteConfirmation.contract.message')}
      />
    </PageContainer>
  )
}
